import { createContext, FC, useMemo, ReactNode, useContext } from "react";
import { useAuth } from "src/hooks/use-auth";
import {
  AxiosError,
  // AxiosRequestConfig
} from "axios";
import { paths } from "src/paths";
import { useRouter } from "src/hooks/use-router";
import toast from "react-hot-toast";
import { handleNetworkSessionExpired } from "src/utils/snug/network-session";

const axios = require("axios").default;

interface EstateAppApiProviderProps {
  children: ReactNode;
}

const EstateAppApiContext = createContext({
  estateAppApiInstance: axios,
});

// interface CustomAxiosRequestConfig extends AxiosRequestConfig {
//   _retry?: boolean;
// }

export const EstateAppApiProvider: FC<EstateAppApiProviderProps> = (props) => {
  const { children } = props;
  const authStuff = useAuth();
  const router = useRouter();
  const estateAppApiInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_SNUG_API_URL,
    });

    instance.interceptors.request.use((config: any) => {
      if (localStorage.getItem("s_at")) {
        config.headers.Authorization = "Bearer " + localStorage.getItem("s_at");
      }
      config.headers["X-User-Domain"] = window.location.host.replace(
        "https://",
        ""
      );
      return config;
    });

    instance.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        //
        // Need to figure this out, we wnt to refresh token if it is expired, but the resulting behavior of the code below for multiple api refresh
        // was for the token to be refreshed but still be directed to login page from some refresh errors. For now we're just signing out, but ideal UX
        // would sipmly be to refresh token and continue the UX without blip.
        //
        // const originalRequest = error.config as CustomAxiosRequestConfig;
        //
        // if (error.response?.status === 401 && !originalRequest._retry) {
        //   originalRequest._retry = true;
        //   try {
        //     await authStuff.s_rt(); // Refresh the token
        //     instance.defaults.headers.common["Authorization"] =
        //       "Bearer " + localStorage.getItem("s_at");
        //     return instance(originalRequest);
        //   } catch (err) {
        //     // Handle error, show message or redirect to login page
        //   }
        // }
        // The firm requires MFA and this session hasn't passed it. Only that policy block carries
        // `enrolled`; other mfa_required 403s (e.g. an admin-only action) are left to their callers.
        const data = error.response?.data as { code?: string; enrolled?: unknown } | undefined;
        if (
          error.response?.status === 403 &&
          data?.code === "mfa_required" &&
          typeof data.enrolled === "boolean"
        ) {
          const returnTo = window.location.pathname + window.location.search;
          if (!data.enrolled) {
            console.log("[estateAppApiInstance] mfa_required → MFA setup", error.config?.url);
            toast("Your firm requires multi-factor authentication. Set it up to continue.", {
              id: "mfa-required",
            });
            if (!window.location.pathname.startsWith(paths.auth.mfaSetup)) {
              router.replace(
                paths.auth.mfaSetup + "?" + new URLSearchParams({ returnTo }).toString()
              );
            }
          } else {
            // They have MFA, but this session predates it: signing in again asks for the code.
            console.log("[estateAppApiInstance] mfa_required → signing out, login", error.config?.url);
            toast("Please sign in again with your verification code.", { id: "mfa-required" });
            await authStuff.signOut();
            router.replace(
              paths.auth.jwt.entryStart + "?" + new URLSearchParams({ returnTo }).toString()
            );
          }
          // Hold the request open: we're navigating away, and callers' error fallbacks (toasts,
          // the user-data create-on-miss) would otherwise fight the redirect.
          return new Promise(() => {});
        }
        if (error.response?.status === 401) {
          // An expired network owner session returns to the home login and the switcher
          // landing instead of signing out. Hold the request open: we're navigating away.
          if (await handleNetworkSessionExpired()) {
            console.log("[estateAppApiInstance] 401 in network session → network landing", error.config?.url);
            return new Promise(() => {});
          }
          console.log("[estateAppApiInstance] 401 → signing out, login", error.config?.url);
          await authStuff.signOut();
          const href = paths.auth.jwt.entryStart;
          router.replace(href);
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [authStuff, router]);

  return (
    <EstateAppApiContext.Provider
      value={{
        estateAppApiInstance,
      }}
    >
      {children}
    </EstateAppApiContext.Provider>
  );
};

export const useEstateAppApiContext = () => useContext(EstateAppApiContext);
